import axios from "axios";
import {
  APP_URL,
  setUserPermissions,
  setUserDetails,
  getAccessToken,
  getPassCode,
  getUserDetails,
} from "config";
import React, { createContext, useEffect, useMemo, useState } from "react";
import api from "services/api";
import { Auth } from "aws-amplify";
import { ACCOUNT_OTHERS } from "utility/constants";

export const AuthContext = createContext({});

const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [permissions, setPermissions] = useState(null);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [isLoggingIn, setIsLoggingIn] = useState(true);

  const [accessToken, setAccessToken] = useState(null);
  const [refreshToken, setRefreshToken] = useState(null);

  const getUserPermission = (token) => {
    api({
      Method: "GET",
      Url: "/auth/list-permissions",
      Headers: {
        Authorization: `Bearer ${token}`,
      },
      ResponseSuccessCallback: (response) => {
        if (response && response?.data) {
          const permission = response.data.data[0]?.permissions;
          setUserPermissions(permission);
          setPermissions(permission);
          setIsLoggingIn(false);
          setIsLoggedIn(true);
        }
      },
    });
  };

  const getAuthenticatedUser = async (token) => {
    try {
      const response = await axios.get(`${APP_URL}/auth/get-user`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response && response?.data) {
        setUser(response.data.data);

        setUserDetails({
          type: response.data.data.type,
        });

        getUserPermission(token);

        return response.data.data;
      }

      return response;
    } catch (err) {
      window.location.href = "/login";
      throw Error(err);
    }
  };

  const restoreSession = () => {
    const token = getAccessToken();

    if (token) {
      getAuthenticatedUser(token);
    }
  };

  const getUser = () => Auth.currentAuthenticatedUser();

  useEffect(() => {
    getUser()
      .then((res) => {
        if (res !== "No current user") {
          restoreSession();
        }
      })
      .catch(() => {
        // User is not authenticated, which is expected for code login users
        // Check if this is a code login user
        // (they have manually set permissions)
        const passCode = getPassCode();
        const userDetails = getUserDetails();

        const isCodeLoginUser =
          passCode || userDetails?.type === ACCOUNT_OTHERS;

        // Only restore session for regular login users, not code login users
        // Code login users have their permissions and user details set manually
        if (!isCodeLoginUser) {
          const token = getAccessToken();

          if (token) {
            restoreSession();
          }
        } else {
          // For code login users, just set isLoggedIn if they have a token
          const token = getAccessToken();

          if (token) {
            setIsLoggedIn(true);
            // Restore user and permissions from localStorage
            // (already set by LoginForm)
            const storedUser = getUserDetails();

            const storedPermissions = JSON.parse(
              localStorage.getItem("USER_PERMISSIONS") || null
            );

            if (storedUser) {
              setUser(storedUser);
            }

            if (storedPermissions) {
              setPermissions(storedPermissions);
            }
          }
        }
      });
  }, []);

  const payload = {
    user: user &&
      permissions && {
        ...user,
        permissions,
      },
    setUser,
    setPermissions,
    getAuthenticatedUser,
    getUserPermission,
    isLoggedIn,
    setIsLoggedIn,
    accessToken,
    setAccessToken,
    refreshToken,
    setRefreshToken,
    isLoggingIn,
    setIsLoggingIn,
  };

  return useMemo(
    () => (
      <AuthContext.Provider value={payload}>{children}</AuthContext.Provider>
    ),
    [permissions, user]
  );
};

export default AuthProvider;
